import * as Sentry from '@sentry/nextjs';
import type { APIResponse, PublicRuntimeConfig } from 'app.types';
import Cookies from 'js-cookie';
import { logger } from 'logs';
import { ServerError } from 'logs/serverError';
import type { GetServerSidePropsContext, NextApiRequest } from 'next';
import type { NextApiResponse } from 'next/dist/shared/lib/utils';
import queryString from 'query-string';

import getConfig from 'next/config';

const { publicRuntimeConfig } = getConfig();

const { app: { version } }: PublicRuntimeConfig = publicRuntimeConfig;

import type {
  ApiPointsFetchParamsType,
  AvailableApiPoints,
} from 'constants/apiPoints';
import apiPoints from 'constants/apiPoints';

// import { startSpan } from 'utils/APMService';
import getBaseUrl from 'utils/getBaseUrl';

export const responseSymbol: unique symbol = Symbol('response');

type Method = 'GET' | 'POST';

export default async function doFetch<
  Key extends keyof ApiPointsFetchParamsType,
>({
  apiPointName,
  body,
  context,
  urlParams,
  queryParams,
  contentType = 'application/json',
  headers = {},
  mode,
}: {
  apiPointName: AvailableApiPoints;
  body: ApiPointsFetchParamsType[Key]['request'];
  context?:
    | GetServerSidePropsContext
    | { req: NextApiRequest; res: NextApiResponse }
    | null;
  urlParams?: Record<string, string | number>;
  queryParams?: ApiPointsFetchParamsType[Key]['queryParams'];
  contentType?: string;
  headers?: Record<string, string>;
  mode?: RequestMode;
}): Promise<
  APIResponse<ApiPointsFetchParamsType[Key]['response']> & {
    [responseSymbol]: Response;
  }
> {
  // const endSpan = startSpan(`fetch ${apiPointName}`);

  let resourceUrl = '';

  try {

    const method = apiPoints[apiPointName].method as Method;
    const initFetchParams: RequestInit = {
      method,
      headers: {
        accept: 'application/json',
        'Content-Type': contentType,
        'accept-language': 'en;q=0.9',
        ...headers,
      },
      credentials: 'include',
    };

    const isClient = typeof window !== 'undefined';
    const isServerSideFetch = !!context && isClient === false;

    if (mode) {
      initFetchParams.mode = mode;
    }

    let { path }: { path: string; getBaseUrlApi?: () => string } = apiPoints[apiPointName];
    const {
      getPath,
      getBaseUrlApi,
    }: { getBaseUrlApi?: () => string; getPath: any } = apiPoints[apiPointName];

    if (getPath && urlParams) {
      path = getPath(urlParams);
    }

    if (queryParams) {
      path = `${path}?${queryString.stringify(queryParams)}`;
    }

    const baseUrl = (getBaseUrlApi?.() as string) || getBaseUrl(isServerSideFetch);
    const resource = (resourceUrl = `${baseUrl}${path}`);

    if (body) {
      initFetchParams.body = JSON.stringify(body);
    }
    
    if (context?.req.headers.cookie) {
      // @ts-ignore
      initFetchParams.headers.Cookie = context.req.headers.cookie;
    }

    const internalUrls = ['billing-external.welltory.com', 'api.welltory.com', 'billing-external-api.billing.svc', 'backend-django-api.monolith.svc'];
    const isInternal = internalUrls.some(domain => baseUrl.includes(domain));


    if (isServerSideFetch && isInternal === true) {
      // @ts-ignore
      initFetchParams.headers['User-Agent'] = `Welltory/monocle/${version}`;

      if (context?.req.headers['x-forwarded-for']) {
        // @ts-ignore
        initFetchParams.headers['X-Forwarded-For'] = context?.req.headers['x-forwarded-for'];
      }
    }

    let csrftoken = '';

    if (context?.req?.cookies?.csrftoken) {
      csrftoken = context.req.cookies.csrftoken;
    } 
    else {
      csrftoken = Cookies.get('csrftoken') as string; // => 'value'
    }

    // @ts-ignore
    initFetchParams.headers['X-CSRFToken'] = csrftoken;

    // let baseUrl = '/';
    //
    // if (typeof window === 'undefined') {
    //   baseUrl = 'http://front:3000/';
    // }

    logger.info({
      key: '[HTTP][START]',
      params: {
        resource: resource,
        // queryParams,
        // headers: {
        //   ...initFetchParams.headers,
        // },
      },
      info: {
        'user-uid': context?.req.headers['x-request-id'],
        'create-date': context?.req.headers['X-request-create-date'],
        application: context?.req.headers['X-request-application'],
        url: baseUrl || 'URL not available',
      },
    });

    const response = await fetch(resource, initFetchParams);

    const isValidResponseCode =
      response.status >= 200 && response.status <= 400;

    if (!isValidResponseCode) {
      const errorData = await response.json();
      const error = errorData.error || errorData.detail || 'http request error';
      
      throw new ServerError(error, {
        resource: resource,
        queryParams,
        initFetchParams,
        response,
      });
      
    }

    logger.info({
      key: '[HTTP][END]',
      params: {
        status: response.status,
        resource: resource,
        // queryParams,
        // headers: {
        //   ...initFetchParams.headers,
        // },
      },
      info: {
        'user-uid': context?.req.headers['x-request-id'],
        'create-date': context?.req.headers['X-request-create-date'],
        application: context?.req.headers['X-request-application'],
        url: baseUrl || 'URL not available',
      },
    });

    if (context) {
      try {
        //https://github.com/node-fetch/node-fetch/issues/251
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const raw = (response.headers as any).raw()['set-cookie'] as
          | string[]
          | undefined;

        if (raw) {
          context.res.setHeader('Set-Cookie', raw);
        }
      } catch (e) {}
    }

    const result = (await response.json()) as Promise<
      APIResponse<ApiPointsFetchParamsType[Key]['response']> & {
        [responseSymbol]: Response;
      }
    >;

    // @ts-ignore
    result[responseSymbol] = response;

    return result;
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    const errorToReject = err instanceof Error ? err : new Error(typeof err === 'object' ? JSON.stringify(err, null, 2) : String(err));
    Sentry.captureException(errorToReject);
    logger.error({
      key: '[HTTP][ERROR]',
      error: errorToReject,
      params: {
        resource: resourceUrl,
      },
      info: {
        'user-uid': context?.req.headers['x-request-id'],
        'create-date': context?.req.headers['X-request-create-date'],
        application: context?.req.headers['X-request-application'],
      },
    });
    Sentry.withScope(function (scope) {
      // group errors together based on their request and response
      scope.setFingerprint([
        apiPoints[apiPointName]?.method || 'unknown-method',
        apiPointName || 'unknown-api-point',
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        errorToReject instanceof Error && 'statusCode' in errorToReject ? String((errorToReject as any).statusCode) : 'unknown-status',
      ]);
      Sentry.captureException(errorToReject);
    });

    return Promise.reject(errorToReject);
  } finally {
    // endSpan();
  }
}

doFetch.responseSymbol = responseSymbol;
